/**
 * ECGUploadModal - Professional modal for uploading ECG result files (PDF/DOCX)
 * 
 * Features:
 * - File upload (PDF/DOCX only)
 * - S3 storage with proper naming convention
 * - Success feedback
 * - Real-time updates to Diagnostics panel
 * 
 * Author: AI Assistant
 * Date: 2025-10-17
 */

import React, { useState } from 'react';
import { X, Upload, FileText, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import apiClient from '../../services/apiClient';
import toast from 'react-hot-toast';

const ECGUploadModal = ({ isOpen, onClose, bookingId, patientName, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setError(null);
    setUploadSuccess(false);
    
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(fileExtension)) {
      setError('Please select a PDF or DOCX file');
      setSelectedFile(null);
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('booking_id', bookingId);
      formData.append('patient_name', patientName);

      const response = await apiClient.post('/ecg-results/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('✅ ECG file uploaded successfully:', response.data);
      
      setUploadSuccess(true);
      toast.success('ECG result uploaded successfully!');

      // Notify parent component to refresh data
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }

      // Auto-close modal after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('❌ ECG upload error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to upload ECG result';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadSuccess(false);
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]" onClick={handleClose}>
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Upload ECG Result</h2>
              <p className="text-purple-100 text-sm">PDF or DOCX format</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            disabled={uploading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Patient Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-gray-600">Patient</p>
            <p className="font-semibold text-gray-900">{patientName}</p>
            <p className="text-xs text-gray-500 mt-1">Booking ID: {bookingId}</p>
          </div>

          {/* Success Message */}
          {uploadSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3 animate-fade-in">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900">Upload Successful!</p>
                <p className="text-sm text-green-700 mt-1">
                  ECG result has been uploaded and is now available in the Diagnostics tab.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Upload Failed</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* File Upload Area */}
          {!uploadSuccess && (
            <div className="space-y-3">
              <label className="block">
                <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                  selectedFile 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-300 hover:border-purple-400 hover:bg-gray-50'
                }`}>
                  <input
                    type="file"
                    accept=".pdf,.docx,.doc"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Upload className={`w-12 h-12 mx-auto mb-3 ${selectedFile ? 'text-purple-600' : 'text-gray-400'}`} />
                  {selectedFile ? (
                    <div>
                      <p className="font-semibold text-purple-900">{selectedFile.name}</p>
                      <p className="text-sm text-purple-600 mt-1">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <p className="text-xs text-gray-500 mt-2">Click to change file</p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium text-gray-700">Click to select ECG result file</p>
                      <p className="text-sm text-gray-500 mt-1">PDF or DOCX (max 10MB)</p>
                    </div>
                  )}
                </div>
              </label>

              {/* File naming info */}
              {selectedFile && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-600 font-medium mb-1">File will be saved as:</p>
                  <p className="text-xs text-gray-800 font-mono break-all">
                    ECG_Result_{patientName.replace(/\s+/g, '_')}_{bookingId}.{selectedFile.name.split('.').pop()}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!uploadSuccess && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className={`px-6 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-all ${
                !selectedFile || uploading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700 hover:shadow-lg'
              }`}
            >
              {uploading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Upload</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ECGUploadModal;

